import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "mt-2 rounded-none bg-[#141414] border-border h-12 focus:ring-2 focus:ring-primary text-white";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto px-5 py-20 rise" data-testid="forgot-password-page">
      <p className="text-xs tracking-[0.25em] uppercase font-bold text-muted-foreground">
        Account recovery
      </p>
      <h1 className="font-display text-4xl font-black tracking-tighter mt-3 mb-8">
        Forgot password
      </h1>

      {sent ? (
        <p data-testid="forgot-password-sent" className="text-sm leading-relaxed text-muted-foreground">
          If that email is registered, a reset link is on its way. The link expires in one hour.
        </p>
      ) : (
        <form onSubmit={submit}>
          <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
            Email
          </Label>
          <Input
            data-testid="forgot-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            placeholder="you@example.com"
          />
          <Button
            type="submit"
            disabled={busy}
            data-testid="forgot-submit-button"
            className="w-full mt-8 h-12 rounded-none bg-primary hover:bg-[#FF5C53] tracking-[0.15em] uppercase text-xs font-bold"
          >
            {busy ? "Sending" : "Send reset link"}
          </Button>
        </form>
      )}

      <p className="mt-6 text-sm text-muted-foreground">
        <Link to="/login" data-testid="back-to-login-link" className="text-primary font-semibold hover:underline">
          Back to sign in
        </Link>
      </p>
    </div>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto px-5 py-20 rise" data-testid="reset-password-page">
      <p className="text-xs tracking-[0.25em] uppercase font-bold text-muted-foreground">
        Account recovery
      </p>
      <h1 className="font-display text-4xl font-black tracking-tighter mt-3 mb-8">
        New password
      </h1>

      {!token ? (
        <p data-testid="reset-token-missing" className="text-sm text-primary">
          This reset link is incomplete. Request a new one.
        </p>
      ) : (
        <form onSubmit={submit}>
          <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
            New password
          </Label>
          <Input
            data-testid="reset-password-input"
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
            placeholder="••••••••"
          />
          {error && (
            <p data-testid="reset-error" className="mt-5 text-sm text-primary font-medium">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid="reset-submit-button"
            className="w-full mt-8 h-12 rounded-none bg-primary hover:bg-[#FF5C53] tracking-[0.15em] uppercase text-xs font-bold"
          >
            {busy ? "Saving" : "Update password"}
          </Button>
        </form>
      )}

      <p className="mt-6 text-sm text-muted-foreground">
        <Link to="/forgot-password" data-testid="request-new-link" className="text-primary font-semibold hover:underline">
          Request a new link
        </Link>
      </p>
    </div>
  );
}
